/**
 * UsdcPaymentPrompt Component
 * 
 * In-app USDC payment via connected wallet.
 * Uses wagmi useWriteContract for ERC-20 transfer.
 * 
 * Polls /api/jobs/{job_id}/payment-status to recover state on refresh.
 * 
 * States:
 * - loading: Initial fetch in progress
 * - unpaid: Ready to pay (shows Pay button)
 * - pending: Transaction sent, waiting for backend confirmation
 * - confirmed: Payment confirmed
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useAccount, useConnect, useSwitchChain, useWriteContract, useReadContract } from 'wagmi';
import { useWallet } from '../contexts/WalletContext';
import { parseUnits } from 'viem';
import { base } from 'wagmi/chains';
import '../styles/UsdcPayment.css';

// Minimal ERC-20 ABI — only what we need
const erc20Abi = [
  {
    name: 'transfer',
    type: 'function',
    stateMutability: 'nonpayable',
    inputs: [
      { name: 'to', type: 'address' },
      { name: 'value', type: 'uint256' },
    ],
    outputs: [{ type: 'bool' }],
  },
  {
    name: 'balanceOf',
    type: 'function',
    stateMutability: 'view',
    inputs: [{ name: 'account', type: 'address' }],
    outputs: [{ type: 'uint256' }],
  },
];

const USDC_CONTRACT = '0x833589fCD6eDb6E08f4c7C32D4f71b54bdA02913';
const USDC_DECIMALS = 6;

// Polling constants
const INITIAL_POLL_INTERVAL = 3000;  // 3 seconds
const MAX_POLL_INTERVAL = 30000;     // 30 seconds
const MAX_POLL_DURATION = 30 * 60 * 1000;  // 30 minutes

export default function UsdcPaymentPrompt({ metadata, confirmed = false, statusUrl, broadcastUrl, onPaymentConfirmed }) {
  const { address, chain } = useAccount();
  const { wallets, primaryWallet, connectedIsLinked, walletsLoading } = useWallet();
  const { connectAsync, connectors } = useConnect();
  const { switchChainAsync } = useSwitchChain();
  const { writeContractAsync, isPending: isSending } = useWriteContract();
  
  // Payment state from backend
  const [paymentState, setPaymentState] = useState('loading');
  const [error, setError] = useState('');
  
  // Broadcasting flag — for the 0-3s window between writeContractAsync and next poll
  const [broadcasting, setBroadcasting] = useState(false);
  
  // Lock pay button during click->sign flow to prevent rapid double-click
  const [payButtonLocked, setPayButtonLocked] = useState(false);
  
  // Guard to fire onPaymentConfirmed exactly once per mount
  const confirmedNotifiedRef = useRef(false);
  
  // Polling refs
  const pollIntervalRef = useRef(INITIAL_POLL_INTERVAL);
  const pollTimerRef = useRef(null);
  const pollStartTimeRef = useRef(null);
  const previousStateRef = useRef(null);

  const {
    treasury_address,
    amount_usdc,
    from_wallet,
    job_id,
  } = metadata || {};

  // Parse required amount to bigint
  const requiredAmount = amount_usdc 
    ? parseUnits(String(amount_usdc), USDC_DECIMALS) 
    : 0n;

  // Balance check — reactive hook
  const { data: balance } = useReadContract({
    address: USDC_CONTRACT,
    abi: erc20Abi,
    functionName: 'balanceOf',
    args: address ? [address] : undefined,
    query: { enabled: !!address },
  });

  const truncateAddress = (addr) => {
    if (!addr) return '';
    return `${addr.slice(0, 6)}...${addr.slice(-4)}`;
  };

  const formatAmount = (amount) => {
    if (!amount) return '0.00';
    const num = parseFloat(amount);
    if (isNaN(num)) return '0.00';
    if (num === Math.floor(num)) return num.toFixed(2);
    return num.toFixed(6).replace(/\.?0+$/, '');
  };

  // Determine effective URLs (custom for intent-based, default for job-based)
  const effectiveStatusUrl = statusUrl || (job_id ? `/api/jobs/${job_id}/payment-status` : null);
  const effectiveBroadcastUrl = broadcastUrl || (job_id ? `/api/jobs/${job_id}/payment-broadcast` : null);

  // Fetch payment status from backend
  const fetchPaymentStatus = useCallback(async () => {
    if (!effectiveStatusUrl) return;
    
    try {
      const res = await fetch(effectiveStatusUrl, {
        credentials: 'include'
      });
      
      if (!res.ok) {
        // Non-critical — keep polling
        console.warn('Payment status fetch failed:', res.status);
        return;
      }
      
      const data = await res.json();
      const newState = data.state;
      
      // Check if state changed — reset polling interval if so
      if (previousStateRef.current !== null && previousStateRef.current !== newState) {
        pollIntervalRef.current = INITIAL_POLL_INTERVAL;
      }
      previousStateRef.current = newState;
      
      setPaymentState(newState);
      
      // Clear broadcasting flag if state advanced
      if (newState === 'pending' || newState === 'confirmed') {
        setBroadcasting(false);
        if (newState === 'confirmed' && onPaymentConfirmed && !confirmedNotifiedRef.current) {
          confirmedNotifiedRef.current = true;
          onPaymentConfirmed();
        }
      }
      
    } catch (err) {
      console.warn('Payment status fetch error:', err);
    }
  }, [effectiveStatusUrl]);

  // Schedule next poll with exponential backoff
  const scheduleNextPoll = useCallback(() => {
    // Stop if confirmed, expired, or unmounted
    if (paymentState === 'confirmed' || paymentState === 'expired') return;
    
    // Stop if max duration exceeded
    if (pollStartTimeRef.current && Date.now() - pollStartTimeRef.current > MAX_POLL_DURATION) {
      console.log('Payment status polling timeout — user can refresh to resume');
      return;
    }
    
    pollTimerRef.current = setTimeout(async () => {
      await fetchPaymentStatus();
      
      // Exponential backoff — double interval up to max
      pollIntervalRef.current = Math.min(pollIntervalRef.current * 2, MAX_POLL_INTERVAL);
      
      scheduleNextPoll();
    }, pollIntervalRef.current);
  }, [paymentState, fetchPaymentStatus]);

  // Start polling on mount
  useEffect(() => {
    if (!effectiveStatusUrl) {
      setPaymentState('unpaid');
      return;
    }
    
    pollStartTimeRef.current = Date.now();
    
    // Initial fetch
    fetchPaymentStatus().then(() => {
      // Start polling after initial fetch
      scheduleNextPoll();
    });
    
    // Cleanup on unmount
    return () => {
      if (pollTimerRef.current) {
        clearTimeout(pollTimerRef.current);
      }
    };
  }, [effectiveStatusUrl, fetchPaymentStatus, scheduleNextPoll]);

  // Stop polling when confirmed or expired
  useEffect(() => {
    if (paymentState === 'confirmed' || paymentState === 'expired') {
      if (pollTimerRef.current) {
        clearTimeout(pollTimerRef.current);
        pollTimerRef.current = null;
      }
    }
  }, [paymentState]);

  const handlePay = async () => {
    setError('');
    setPayButtonLocked(true);  // Lock immediately to prevent rapid double-click

    try {
      // 1. Check chain — switch to Base if needed
      if (chain?.id !== base.id) {
        await switchChainAsync({ chainId: base.id });
      }

      // 2. Check balance (bigint comparison)
      if (balance !== undefined && balance < requiredAmount) {
        setPayButtonLocked(false);  // Unlock so user can fix and retry
        const have = Number(balance) / 1e6;
        const need = Number(requiredAmount) / 1e6;
        setError(`Insufficient USDC balance (have ${have.toFixed(2)}, need ${need.toFixed(2)})`);
        return;
      }

      // 3. Send ERC-20 transfer — capture the hash
      const txHash = await writeContractAsync({
        address: USDC_CONTRACT,
        abi: erc20Abi,
        functionName: 'transfer',
        args: [treasury_address, requiredAmount],
      });

      // 4. Set broadcasting flag IMMEDIATELY for instant UI feedback
      // This must happen before any network call so the user sees the
      // state flip the instant they sign in MetaMask.
      setBroadcasting(true);
      pollIntervalRef.current = INITIAL_POLL_INTERVAL;

      // 5. Record broadcast intent on backend (fire-and-forget — non-fatal on failure)
      // The scanner will still pick up the on-chain tx within 30-90s even if
      // this POST fails. The POST is purely for refresh-recovery — if it lands,
      // a refresh during the pending window correctly shows "Sending..."; if it
      // doesn't land, refresh-recovery degrades but on-chain settlement still works.
      // Do NOT await — we don't want to block the UI on this network call.
      if (effectiveBroadcastUrl) {
        fetch(effectiveBroadcastUrl, {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ tx_hash: txHash }),
        }).catch((postErr) => {
          console.warn('Failed to record broadcast intent (non-fatal):', postErr);
        });
      }

    } catch (e) {
      setPayButtonLocked(false);  // Unlock so user can retry
      // User rejected or error
      if (e?.name === 'UserRejectedRequestError' || e?.message?.includes('User rejected')) {
        setError('Payment cancelled — click Pay to try again');
      } else {
        setError(e?.shortMessage || e?.message || 'Transaction failed');
        // Report failure to backend — resets Card 1 buttons
        // Only intent_id is needed — backend looks up conversation_id from the intent row
        try {
          await fetch('/api/chat/payments/usdc-failed', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
            body: JSON.stringify({
              intent_id: metadata?.intent_id,
              error: e?.shortMessage || e?.message || 'Transaction failed',
            }),
          });
        } catch (reportErr) {
          console.warn('Failed to report USDC failure:', reportErr);
        }
      }
    }
  };

  // State: Expired (intent timed out)
  if (paymentState === 'expired') {
    return (
      <div className="usdc-payment-prompt payment-expired">
        <div className="chain-warning">⚠️ BASE MAINNET ONLY</div>
        <div className="expired-content">
          <span className="expired-icon">⏰</span>
          <h4>Payment Expired</h4>
          <p className="expired-message">
            This payment link has timed out. Ask in chat for a new hosting payment link.
          </p>
        </div>
      </div>
    );
  }

  // State: Confirmed (via polling or prop)
  if (confirmed || paymentState === 'confirmed' || metadata?.already_confirmed) {
    return (
      <div className="usdc-payment-prompt payment-confirmed">
        <div className="confirmed-content">
          <span className="confirmed-icon">✓</span>
          <h4>Payment Confirmed</h4>
          <p>Your project is starting now.</p>
        </div>
      </div>
    );
  }

  // State: Pending (backend has seen the tx) OR Broadcasting (wallet just returned)
  if (paymentState === 'pending' || broadcasting) {
    return (
      <div className="usdc-payment-prompt payment-sent">
        <div className="sent-content">
          <div className="sent-spinner"></div>
          <h4>{broadcasting ? 'Broadcasting Transaction...' : 'Transaction Sent'}</h4>
          <p>Waiting for confirmation...</p>
          <p className="sent-hint">Usually within a couple of minutes</p>
        </div>
      </div>
    );
  }

  // State: Loading (initial fetch in progress)
  if (paymentState === 'loading') {
    return (
      <div className="usdc-payment-prompt payment-loading">
        <div className="loading-content">
          <div className="sent-spinner"></div>
          <p>Loading payment status...</p>
        </div>
      </div>
    );
  }

  // Edge case: No wallet connected
  if (!address) {
    return (
      <div className="usdc-payment-prompt no-wallet">
        <div className="chain-warning">⚠️ BASE MAINNET ONLY</div>
        <div className="payment-amount">
          Send exactly <strong>{formatAmount(amount_usdc)} USDC</strong>
        </div>
        <div className="wallet-connect-area">
          <button
            className="pay-button connect-wallet-btn"
            onClick={async () => {
              try {
                setError('');
                const connector = connectors.find(c => c.type === 'injected') || connectors[0];
                if (connector) {
                  await connectAsync({ connector });
                }
              } catch (e) {
                if (!e?.message?.includes('User rejected')) {
                  setError('Failed to connect wallet. Try again.');
                }
              }
            }}
          >
            🔗 Connect Wallet to Pay
          </button>
          {error && <div className="payment-error">{error}</div>}
        </div>
      </div>
    );
  }

  // Wallet is connected but NOT linked to this account
  if (address && !walletsLoading && !connectedIsLinked) {
    return (
      <div className="usdc-payment-prompt wallet-not-linked">
        <div className="chain-warning">⚠️ BASE MAINNET ONLY</div>
        <div className="payment-amount">
          Send exactly <strong>{formatAmount(amount_usdc)} USDC</strong>
        </div>
        <div className="wallet-guard-message">
          <p className="guard-warning">
            ⚠️ <code>{truncateAddress(address)}</code> is not linked to your account.
          </p>
          <p className="guard-hint">
            Add it in your <a href="/profile">Profile</a>, or switch to a linked wallet in MetaMask.
          </p>
        </div>
      </div>
    );
  }

  // State: Unpaid — Ready to pay
  return (
    <div className="usdc-payment-prompt">
      <div className="chain-warning">⚠️ BASE MAINNET ONLY</div>
      
      <div className="payment-amount">
        Send exactly <strong>{formatAmount(amount_usdc)} USDC</strong>
      </div>

      {error && (
        <div className="payment-error">{error}</div>
      )}

      <button
        className="pay-button"
        onClick={handlePay}
        disabled={isSending || payButtonLocked}
      >
        {isSending ? 'Sending...' : `Pay ${formatAmount(amount_usdc)} USDC`}
      </button>

      <p className="switch-hint">Want to pay by card instead? Just ask me in the chat.</p>

      <div className="payment-footer">
        <div className="wallet-picker">
          <div className="wallet-picker-active">
            <span className="picker-label">Paying from:</span>
            <code className="picker-address">{truncateAddress(address)}</code>
            {primaryWallet && address?.toLowerCase() === primaryWallet.wallet_address.toLowerCase() && (
              <span className="picker-badge primary-badge">Primary</span>
            )}
            <span className="picker-badge linked-badge">✓ Linked</span>
          </div>
          {wallets.length > 1 && (
            <div className="wallet-picker-others">
              <p className="picker-others-label">Other linked wallets:</p>
              {wallets
                .filter(w => w.wallet_address.toLowerCase() !== address.toLowerCase())
                .map(w => (
                  <div key={w.id} className="picker-other-wallet">
                    <code>{truncateAddress(w.wallet_address)}</code>
                    {w.is_primary && <span className="picker-badge primary-badge">Primary</span>}
                    <span className="picker-hint">Switch in MetaMask to use</span>
                  </div>
                ))
              }
            </div>
          )}
        </div>
        <p className="confirmation-hint">
          Your payment usually confirms within a couple of minutes
        </p>
      </div>
    </div>
  );
}
